import type { AppEnv } from '@brightdrop/app-config';
import { usePermissions, useRole } from '@gm-commercial/profile-context';
import type { Permission, Role } from '@gm-commercial/profile-model';
import {
  Children,
  createElement,
  Fragment,
  type ReactNode,
  useMemo,
} from 'react';

const renderChildren = (children?: ReactNode) => {
  if (!children) {
    return null;
  }

  // Arrays and strings are the only children with a length to fan out over
  const hasMultipleChildren =
    (Array.isArray(children) || typeof children === 'string') &&
    children.length > 1;

  return hasMultipleChildren
    ? createElement(Fragment, null, ...children)
    : Children.only(children); // verifies single child and returns, or throws error
};

/** Children may be a render prop receiving whether the caller passed the checks. */
type SecureWrapperChildren = ReactNode | ((allowed: boolean) => ReactNode);

interface SecureWrapperProps {
  requiredPermissions: Permission[];
  requiredRoles?: Role[];
  allowedEnvironments?: AppEnv[];
  excludedEnvironments?: AppEnv[];
  excludedRoles?: Role[];
  passThrough?: boolean;
  actingRole?: boolean;
  children?: SecureWrapperChildren;
}

const SecureWrapper = ({
  requiredPermissions,
  requiredRoles,
  allowedEnvironments,
  excludedEnvironments,
  excludedRoles,
  passThrough,
  actingRole = true,
  children,
}: SecureWrapperProps): JSX.Element => {
  const { hasPermission } = usePermissions();
  const { hasRole, roleExcluded } = useRole(actingRole);

  const environmentAllowed = useMemo(() => {
    const appEnv = globalThis.appConfig.env;
    return (
      (!allowedEnvironments ||
        (Array.isArray(allowedEnvironments) &&
          allowedEnvironments.includes(appEnv))) &&
      (!excludedEnvironments ||
        (Array.isArray(excludedEnvironments) &&
          !excludedEnvironments.includes(appEnv)))
    );
  }, [allowedEnvironments, excludedEnvironments]);

  const permsAllowed = useMemo(
    () => hasPermission(requiredPermissions),
    [hasPermission, requiredPermissions]
  );

  const roleAllowed = useMemo(
    () =>
      (!requiredRoles ||
        (Array.isArray(requiredRoles) && hasRole(requiredRoles))) &&
      (!excludedRoles ||
        (Array.isArray(excludedRoles) && !roleExcluded(excludedRoles))),
    [hasRole, roleExcluded, requiredRoles, excludedRoles]
  );

  const childElements = useMemo(
    () =>
      typeof children === 'function'
        ? children(permsAllowed && roleAllowed)
        : children,
    [children, permsAllowed, roleAllowed]
  );

  return (
    <>
      {passThrough || (permsAllowed && roleAllowed && environmentAllowed)
        ? renderChildren(childElements)
        : null}
    </>
  );
};

export default SecureWrapper;
